import { z } from 'zod';
import { api } from './client.ts';
import { idSchema } from '../../shared/schemas/common.ts';
import {
  inboxActionTargetSchema,
  inboxQuoteTargetSchema,
  inboxSuggestionIdSchema,
  inboxActionReceiptSchema,
  proposedInboxMoveSchema,
  proposedVisitInstructionSchema,
  proposedInboxQuoteSchema,
  suggestInboxActionAnySchema,
  decideInboxActionAnySchema,
  type InboxActionKind,
  type InboxActionReceipt,
  type InboxActionSuggestionAny,
} from '../../shared/inbox-actions.ts';
import { dateTimeFormat } from '../../shared/schemas/formats.ts';

export type EditableInboxKind = 'visit_instruction' | 'create_quote';
const common = {
  id: inboxSuggestionIdSchema,
  status: z.enum(['pending', 'approved', 'dismissed']),
  expiresAt: z.iso.datetime(dateTimeFormat()),
  createdAt: z.iso.datetime(dateTimeFormat()),
};
const suggestionSchema = z.union([
  z
    .object({ ...common, appointmentId: idSchema, proposal: proposedInboxMoveSchema, target: inboxActionTargetSchema })
    .strict(),
  z
    .object({
      ...common,
      appointmentId: idSchema,
      proposal: proposedVisitInstructionSchema,
      target: inboxActionTargetSchema,
      receipt: inboxActionReceiptSchema.optional(),
    })
    .strict(),
  z
    .object({
      ...common,
      appointmentId: z.null(),
      proposal: proposedInboxQuoteSchema,
      target: inboxQuoteTargetSchema,
      receipt: inboxActionReceiptSchema.optional(),
    })
    .strict(),
]);

/** An approved new kind needs the canonical receipt, including its selected visit. */
export function parseInboxSuggestion(value: unknown, kind?: InboxActionKind): InboxActionSuggestionAny {
  const result = suggestionSchema.parse(value);
  if (kind && result.proposal.kind !== kind) throw new Error('Unexpected suggestion kind');
  if (result.proposal.kind !== 'reschedule') {
    const receipt = 'receipt' in result ? result.receipt : undefined;
    if ((result.status === 'approved') !== Boolean(receipt)) throw new Error('Suggestion receipt status changed');
    if (
      receipt &&
      (receipt.kind !== result.proposal.kind ||
        (receipt.kind === 'visit_instruction' && receipt.appointmentId !== result.appointmentId))
    )
      throw new Error('Suggestion receipt target changed');
  }
  return result as InboxActionSuggestionAny;
}

const contextSchema = z
  .object({
    sourceMessageId: idSchema.nullable(),
    visits: z
      .array(
        z
          .object({
            id: idSchema,
            scheduled_date: z.string(),
            start_time_local: z.string(),
            duration_minutes: z.number().int().positive(),
            timezone: z.string().min(1),
          })
          .strict(),
      )
      .max(20),
    suggestions: z.array(z.unknown()).max(30),
  })
  .strict();
export interface InboxKindContext extends Omit<z.infer<typeof contextSchema>, 'suggestions'> {
  suggestions: InboxActionSuggestionAny[];
}
export function parseInboxKindContext(value: unknown, kind: EditableInboxKind): InboxKindContext {
  const context = contextSchema.parse(value);
  if (kind === 'create_quote' && context.visits.length) throw new Error('A quote draft cannot select a visit');
  return { ...context, suggestions: context.suggestions.map((row) => parseInboxSuggestion(row, kind)) };
}
const decisionSchema = z
  .object({
    status: z.enum(['approved', 'dismissed']),
    alreadyDecided: z.boolean().optional(),
    receipt: inboxActionReceiptSchema.optional(),
  })
  .strict();
export function parseInboxDecision(value: unknown, suggestion: InboxActionSuggestionAny) {
  const result = decisionSchema.parse(value);
  if (suggestion.proposal.kind === 'reschedule') {
    if (result.receipt) throw new Error('Unexpected visit move receipt');
  } else {
    if ((result.status === 'approved') !== Boolean(result.receipt))
      throw new Error('Canonical action receipt is required');
    if (
      result.receipt &&
      (result.receipt.kind !== suggestion.proposal.kind ||
        (result.receipt.kind === 'visit_instruction' && result.receipt.appointmentId !== suggestion.appointmentId))
    )
      throw new Error('Canonical action receipt target changed');
  }
  return result;
}
export function instructionReceipt(receipt?: InboxActionReceipt) {
  return receipt?.kind === 'visit_instruction' ? receipt : undefined;
}
export const inboxKindActionsApi = {
  context: async (conversationId: string, kind: EditableInboxKind) =>
    parseInboxKindContext(
      await api.get<unknown>(`/inbox-actions/conversations/${encodeURIComponent(conversationId)}?kind=${kind}`),
      kind,
    ),
  suggest: async (conversationId: string, input: z.infer<typeof suggestInboxActionAnySchema>) => {
    const body = suggestInboxActionAnySchema.parse(input);
    const result = z
      .object({ suggestion: z.unknown().nullable(), confirmPath: z.string().optional() })
      .strict()
      .parse(
        await api.post<unknown>(`/inbox-actions/conversations/${encodeURIComponent(conversationId)}/suggest`, body),
      );
    const suggestion =
      result.suggestion === null ? null : parseInboxSuggestion(result.suggestion, body.kind ?? 'reschedule');
    if (
      suggestion &&
      (suggestion.id !== body.id || ('appointmentId' in body && suggestion.appointmentId !== body.appointmentId))
    )
      throw new Error('Generated suggestion target changed');
    return { ...result, suggestion };
  },
  confirm: async (id: string, token: string) => {
    const result = z
      .object({ suggestion: z.unknown() })
      .strict()
      .parse(
        await api.get<unknown>(`/inbox-actions/${encodeURIComponent(id)}/confirm?token=${encodeURIComponent(token)}`),
      );
    const suggestion = parseInboxSuggestion(result.suggestion);
    if (suggestion.id !== id) throw new Error('Confirmation suggestion identity changed');
    return { suggestion };
  },
  decide: async (suggestion: InboxActionSuggestionAny, input: z.infer<typeof decideInboxActionAnySchema>) => {
    const body = decideInboxActionAnySchema.parse(input);
    if (suggestion.proposal.kind === 'visit_instruction' && body.editedNotes !== undefined)
      throw new Error('Unexpected quote notes');
    if (suggestion.proposal.kind === 'create_quote' && body.editedText !== undefined)
      throw new Error('Unexpected visit instruction');
    if (suggestion.proposal.kind === 'reschedule' && (body.editedText !== undefined || body.editedNotes !== undefined))
      throw new Error('Unexpected visit move text');
    const result = parseInboxDecision(
      await api.post<unknown>(`/inbox-actions/${encodeURIComponent(suggestion.id)}/decision`, body),
      suggestion,
    );
    if (result.status !== (body.decision === 'approve' ? 'approved' : 'dismissed'))
      throw new Error('Action decision changed');
    return result;
  },
};
